import { useEffect, useCallback } from 'react';

import {
  useApiGet,
  useApiPatch,
  useApiPost,
  useApiDelete,
  useApiPut
} from '../../common/api/Api.hooks';
import { PaginatedList } from '../../common/PaginatedList.interface';
import { Role } from '../roles/Role.interface';
import { Group } from './Group.interface';

export function useFindGroups(shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<PaginatedList<Group>>(
    'groups'
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return { groupList: data, isLoading, error, get };
}

export function useCreateGroup() {
  const { data, isLoading, error, post } = useApiPost<Group>('groups');
  return { group: data, isLoading, error, post };
}

export function useFindGroup(id: number, shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<Group>(`groups/${id}`);
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, id, shouldFetchOnMount]);
  return { group: data, isLoading, error, get };
}

export function useUpdateGroup(id: number) {
  const { data, isLoading, error, patch } = useApiPatch<Group>(`groups/${id}`);
  return { group: data, isLoading, error, patch };
}

export function useDeleteGroup(id: number) {
  const { data, isLoading, error, del } = useApiDelete<Group>(`groups/${id}`);
  return { group: data, isLoading, error, del };
}

export function useGetGroupsByIds(ids: number[], shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<PaginatedList<Group>>(
    'groups'
  );
  const getGroups = useCallback(() => {
    get({ ids: ids.join(','), limit: 100 });
  }, [get, ids]);
  useEffect(() => {
    if (shouldFetchOnMount) {
      getGroups();
    }
  }, [get, getGroups, ids, shouldFetchOnMount]);
  return {
    groups: data,
    isLoading,
    error,
    getGroups
  };
}

export function useFindGroupRoles(groupId: number) {
  const { data, isLoading, error, get } = useApiGet<Role[]>(
    `groups/${groupId}/roles`
  );
  return {
    groupRoles: data,
    loadingGroupRoles: isLoading,
    errorLoadingGroupRoles: error,
    findGroupRoles: get
  };
}

export function useAddRolesToGroup(groupId: number) {
  const { data, isLoading, error, put } = useApiPut<Role[]>(
    `groups/${groupId}/roles`
  );
  const addRolesToGroup = useCallback(
    (roleId: number | number[], applyUserRoleChanges: boolean) =>
      put({
        roleIds: Array.isArray(roleId) ? roleId : [roleId],
        applyUserRoleChanges
      }),
    [put]
  );
  return {
    updatedGroupRoles: data,
    updatingGroupRoles: isLoading,
    errorUpdatingGroupRoles: error,
    addRolesToGroup
  };
}

export function useRemoveRolesFromGroup(groupId: number) {
  const { data, isLoading, error, del } = useApiDelete<Role[]>(
    `groups/${groupId}/roles`
  );
  const removeUsersFromGroup = useCallback(
    (roleId: number | number[], applyUserRoleChanges: boolean) =>
      del({ roleId, applyUserRoleChanges }),
    [del]
  );
  return {
    updatedGroupRoles: data,
    updatingGroupRoles: isLoading,
    errorUpdatingGroupRoles: error,
    removeUsersFromGroup
  };
}

export function useAddUsersToGroup(groupId: number) {
  const { data, isLoading, error, put } = useApiPut<Group>(
    `groups/${groupId}/users`
  );
  const addUsersToGroup = useCallback(
    (userId: number | number[], applyUserRoleChanges: boolean) =>
      put({
        userIds: Array.isArray(userId) ? userId : [userId],
        applyUserRoleChanges
      }),
    [put]
  );
  return {
    data,
    isLoading,
    error,
    addUsersToGroup
  };
}

export function useRemoveUsersFromGroup(groupId: number) {
  const { data, isLoading, error, del } = useApiDelete<Group>(
    `groups/${groupId}/users`
  );
  const removeUsersFromGroup = useCallback(
    (userId: number | number[], applyUserRoleChanges: boolean) =>
      del({ userId, applyUserRoleChanges }),
    [del]
  );
  return {
    data,
    isLoading,
    error,
    removeUsersFromGroup
  };
}
